import { HStack, Icon, Link, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  ISubscription,
  IdentityVerificationStatus,
  getBrand,
  getHostName,
  isFeatureEnabled,
  routes,
} from '@blockpulse3/data/shared';
import { CompanyStatus, IdentityType, useGetCompanyQuery } from '@blockpulse3/graphql/hooks';
import {
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
  PhoneBanner,
} from '@blockpulse3/ui/commons';
import { OptionList } from '@blockpulse3/web-client/option';
import { Wallet } from '@blockpulse3/web-client/shareholding';

import { FundraisingList } from './FundraisingList';
import { IdentityHeader } from './IdentityHeader';
import { InvitationList } from './InvitationList';
import { CompanyVerificationModal } from './VerificationModal';

type Props = unknown;

/**
 * CompanyDashboardView.
 * Dashboard of the company from the active workspace.
 *
 * @returns {JSX.Element}
 */
export function CompanyDashboardView(): JSX.Element {
  const i18nIdentityHeader = useTranslations('IdentityHeader');

  const [subscriptionId, setSubscriptionId] = useState<string>('');

  const navigate = useNavigate();

  const editDisclosure = useDisclosure();
  const verifModalDisclosure = useDisclosure();

  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const renderPhoneBanner = isFeatureEnabled(getBrand(getHostName()), 'phoneBanner');

  const handleInvitationClick = (subscription: ISubscription): void => {
    if (!company) return;

    if (company.status === CompanyStatus.RUNNING) {
      if (subscription.identity.verificationStatus === IdentityVerificationStatus.APPROVED) {
        navigate(routes.workspace.subscription.fmt(company.id, subscription.id));
      } else {
        setSubscriptionId(subscription.id);
        verifModalDisclosure.onOpen();
      }
    } else {
      navigate(routes.onboardings.company.infos.fmt(company.id));
    }
  };

  const handleVerificationModalClose = (): void => {
    setSubscriptionId('');
    verifModalDisclosure.onClose();
  };

  const handleVerificationModify = (): void => {
    if (company) navigate(routes.workspace.settings.fmt(company.id));
  };

  const isVerified = company?.kybVerificationStatus === IdentityVerificationStatus.APPROVED;

  return (
    <Stack spacing="6">
      <IdentityHeader>
        <IdentityCard onMouseEnter={editDisclosure.onOpen} onMouseLeave={editDisclosure.onClose}>
          <IdentityAvatar
            boxSize="14"
            src={company?.profilePicture || ''}
            type={IdentityType.COMPANY}
          />
          <Stack spacing="0">
            <HStack spacing="3">
              <IdentityCardTitle isChecked={isVerified}>
                <Text>{company?.name && `${company.name} (${company.registrationNumber})`}</Text>
              </IdentityCardTitle>
              {editDisclosure.isOpen && (
                <HStack fontSize="sm" spacing="1">
                  <Icon as={PencilIcon} />
                  <Link fontWeight="400" href={routes.workspace.settings.fmt(companyId)}>
                    {i18nIdentityHeader('EditInfos')}
                  </Link>
                </HStack>
              )}
              <Link
                display={{ base: 'block', md: 'none' }}
                fontWeight="400"
                href={routes.me.settings.href}
              >
                <Icon as={PencilIcon} />
              </Link>
            </HStack>
            <IdentityCardDescription>
              <Text>{company?.address?.formatted}</Text>
            </IdentityCardDescription>
          </Stack>
        </IdentityCard>
      </IdentityHeader>

      {renderPhoneBanner && <PhoneBanner />}
      <Wallet />
      <FundraisingList />
      <OptionList />
      <InvitationList onInvitationClick={handleInvitationClick} />
      <CompanyVerificationModal
        isOpen={verifModalDisclosure.isOpen}
        subscriptionId={subscriptionId}
        onCancel={handleVerificationModalClose}
        onClose={handleVerificationModalClose}
        onModify={handleVerificationModify}
      />
    </Stack>
  );
}

export type CompanyDashboardViewProps = Props;
